import React, { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose } from 'redux'
import pickby from 'lodash.pickby'
import identity from 'lodash.identity'

import { Testable } from '@cred/react-testable'
import { withStyles } from '@cred/react-theme'

import CtaButton from 'components/cta-button'
import withReduxProvider from 'components/hocs/withReduxProvider'
import withApolloProvider from 'components/hocs/withApolloProvider'

import FilterModal from '../components/filter-modal'
import Dashboard from '../../../mortgage-combined/rate-table/widget/dashboard/App'
import MortgageFilterForm from '../../../mortgage-combined/rate-table/widget/filter-form'
import useProductsAndFormHandler from '../../../mortgage-combined/rate-table/widget/shared/hooks/useProductsAndFormHandler'
import defaultStyles from '../../../mortgage-combined/rate-table/themes/default.mutlivertical.module.scss'
import {
  PURCHASE_TYPE,
  REFINANCE_TYPE,
  MORTGAGE_FILTER_FORM_NAME,
} from '../../../mortgage-combined/rate-table/widget/common/constants'
import {
  generateOnGetStartedCallback,
  getFilterText,
  mortgageWidgetMapStateToProps,
} from '../../../mortgage-combined/rate-table/widget/common/utils'

const paginationName = 'mortgage-rate-table'

const MortgageRateTable = (props) => {
  const {
    /* eslint-disable-next-line */
      utms: { utm_campaign, utm_source, utm_medium, utm_content},
    partnerFormData,
    initialValues,
    sortBy,
    termState,
    loanTypeState,
    isPurchase,
    styles,
    variation,
    metaQuery,
    formData = {},
    page = 0,
    omitUtmParams = false,
  } = props
  const [submitFormValues, formValues, currentProducts, loadingProducts, error] =
    useProductsAndFormHandler({ initialValues, partnerFormData, isMultiVertical: true })
  const termFilters = Object.entries(termState)
    .filter((v) => v[1])
    .map((v) => v[0])
  const utmParams = pickby(
    {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
    },
    identity,
  )
  const onGetStarted = generateOnGetStartedCallback({
    utmParams,
    isPurchase,
    omitUtmParams,
    metaQuery,
  })
  const LENDER_PER_PAGE = useMemo(() => (window.innerWidth < 568 ? 2 : 3), [window.innerWidth])

  // ARM disabled tmp
  const products = (currentProducts[loanTypeState] || []).filter(
    ({ productType }) => productType !== 'fiveOneARM',
  )
  const filteredProducts = termFilters.length
    ? products.filter(({ productType }) => termFilters.some((e) => e === productType))
    : products
  const sortedProducts = filteredProducts.sort((p1, p2) => p1[sortBy] - p2[sortBy])
  const bestProducts = sortedProducts.slice(0, 8)
  const [startAtFront, setStartAtFront] = useState(true)

  const paginationProps = {
    paginationName,
    variation,
    page,
    LENDER_PER_PAGE,
  }

  const filterFormProps = {
    loadingProducts,
    initialValues: formValues,
    form: MORTGAGE_FILTER_FORM_NAME,
    keepDirtyOnReinitialize: true,
  }

  useEffect(() => {
    // startAtFront is true after render, reset it
    if (startAtFront) setStartAtFront(false)
  }, [startAtFront])

  const generateSubmit = (submitFormValuesFn, closeModalFn) => () => {
    submitFormValuesFn(formData)
    closeModalFn()
    setStartAtFront(true)
  }

  return (
    <Testable testId="rate-widget-container">
      <FilterModal
        formName={MORTGAGE_FILTER_FORM_NAME}
        filterText={getFilterText(formValues, termState, isPurchase)}
      >
        {({ closeModal }) => (
          <MortgageFilterForm
            {...filterFormProps}
            withFooter
            cancel={(e) => {
              e.preventDefault()
              closeModal()
            }}
            onSubmit={generateSubmit(submitFormValues, closeModal)}
            isMultiVertical
          />
        )}
      </FilterModal>
      {loadingProducts && <div className={styles.overlay} />}
      <Dashboard
        onGetStarted={onGetStarted}
        error={error}
        loading={loadingProducts}
        products={bestProducts}
        isFullTable
        isMultiVertical
        multiVerticalVariation={variation}
        analyticsContexts={[]}
        initialFormValues={formValues}
        paginationProps={paginationProps}
      />
      {!loadingProducts && bestProducts.length === 0 && (
        <div className={styles.compareRatesSection}>
          <CtaButton onClick={onGetStarted}>Compare Rates</CtaButton>
        </div>
      )}
    </Testable>
  )
}

MortgageRateTable.propTypes = {
  variation: PropTypes.string.isRequired,
  theme: PropTypes.string.isRequired,
  formData: PropTypes.object,
  utms: PropTypes.object.isRequired,
  partnerFormData: PropTypes.object,
  initialValues: PropTypes.object,
  styles: PropTypes.object.isRequired,
  sortBy: PropTypes.string,
  termState: PropTypes.object,
  loanTypeState: PropTypes.oneOf([PURCHASE_TYPE, REFINANCE_TYPE]),
  isPurchase: PropTypes.bool,
  page: PropTypes.number,
  omitUtmParams: PropTypes.bool,
  metaQuery: PropTypes.string,
}

export { MortgageRateTable as MortgageRateTableNoHoc }

export default compose(
  withApolloProvider,
  withReduxProvider,
  withStyles(defaultStyles),
  connect(mortgageWidgetMapStateToProps),
)(MortgageRateTable)
